import { Flex, Space, Title } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useAuth } from '../../contexts/AuthProvider';
import { useI18n } from '../../contexts/I18nProvider';
import ScreenWrapper from '../../core/layouts/components/ScreenWrapper';
import PayslipService from '../../services/PayslipService';
import GmaLegacyPayslipsAlert from './components/GmaLegacyPayslipsAlert';
import HistoPaieConnector from './components/HistoPaieConnector';
import PayslipsUserTable from './components/PayslipsUserTable';
import RecentPayslipsUser from './components/RecentPayslipsUser';
import { isPayslipAvailable } from './utils/payslipAvailability';

export default function Payslips() {
  const { user } = useAuth();
  const { userId } = useParams();
  const { t } = useI18n();

  const { data: payslips, isLoading: isPayslipsUserDataLoading } = useQuery({
    queryKey: ['PayslipService.getUserPayslips', userId],
    queryFn: () => PayslipService.getUserPayslips(userId, false),
  });

  // Les aperçus récents chargent le PDF dès le montage : ne leur donner que des
  // bulletins réellement servables, sinon l'iframe reste vide sans explication.
  const availablePayslips = useMemo(
    () => (payslips ?? []).filter(isPayslipAvailable),
    [payslips]
  );

  const hasLegacyGmaPayslips = useMemo(
    () => (payslips ?? []).some((payslip) => !isPayslipAvailable(payslip)),
    [payslips]
  );

  return (
    <ScreenWrapper
      title={
        <Flex gap="md" align="center" justify="center">
          <Title order={3}>{t('w.payslips')}</Title>
          {!!userId && <HistoPaieConnector userId={userId} />}
        </Flex>
      }
    >
      {hasLegacyGmaPayslips && <GmaLegacyPayslipsAlert />}
      {!!availablePayslips.length && (
        <>
          <RecentPayslipsUser
            myPayslips={availablePayslips}
            userId={userId}
            userName={user.fullname!}
            companyId={user.company?.id!}
          />
          <Space h="xl" />
        </>
      )}
      {payslips && (
        <PayslipsUserTable
          userName={user.fullname!}
          payslips={payslips}
          userId={userId}
          companyId={user.company?.id!}
          loading={isPayslipsUserDataLoading}
        />
      )}
    </ScreenWrapper>
  );
}
